import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { SpinnerLoader } from "@/components/ui/Loader";
import { useBreakpoint } from "@/utils/hooks/useBreakpoints";
import { useUserExercises } from "@/data/exercises";
import {
  useExerciseAction,
  useExerciseState,
} from "@/contexts/exercise/Context";
import {
  ExerciseCategoryData,
  ExerciseOptionTypes,
  ExercisesContainerProps,
} from "@/types";
import RenderExerciseComponent from "./Components/RenderExerciseComponent";

const exerciseSequence: ExerciseOptionTypes[] = [
  ExerciseOptionTypes.MultipleChoice,
  ExerciseOptionTypes.TrueFalse,
  ExerciseOptionTypes.MatchingOption,
  ExerciseOptionTypes.GapFill,
  ExerciseOptionTypes.Grouping,
];

const ExercisesContainer: FC<ExercisesContainerProps> = ({ exercise }) => {
  const { currentIndex, isExerciseStarted } = useExerciseState();
  const { setTotalExercises } = useExerciseAction();
  const { isLoading, error } = useUserExercises();
  const breakpoint = useBreakpoint();
  const isSmall = ["xs", "sm", "md"].indexOf(breakpoint) !== -1;

  const getExerciseDataByType = useCallback(
    (type: ExerciseOptionTypes | null): ExerciseCategoryData[] | null => {
      if (!type) return null;
      let data: ExerciseCategoryData[] = [];

      switch (type) {
        case ExerciseOptionTypes.MultipleChoice:
          data = exercise?.multiple_choice || [];
          break;
        case ExerciseOptionTypes.TrueFalse:
          data = exercise?.true_false || [];
          break;
        case ExerciseOptionTypes.MatchingOption:
          data = exercise?.matching_option || [];
          break;
        case ExerciseOptionTypes.GapFill:
          data = exercise?.gap_fill || [];
          break;
        case ExerciseOptionTypes.Grouping:
          data = (exercise?.groupings || []).filter(
            (item) =>
              typeof item === "object" &&
              item !== null &&
              "id" in item &&
              "category" in item &&
              "word" in item,
          );
          break;
        default:
          return null;
      }

      return data.length > 0 ? data : null;
    },
    [exercise],
  );

  const availableExerciseTypes = useMemo(
    () => exerciseSequence.filter((e) => getExerciseDataByType(e) != null),
    [exerciseSequence, getExerciseDataByType],
  );

  const hasSetTotal = useRef(false);

  const currentExerciseType = availableExerciseTypes[currentIndex];

  const exerciseData = getExerciseDataByType(currentExerciseType);

  useEffect(() => {
    if (!hasSetTotal.current && availableExerciseTypes.length > 0) {
      setTotalExercises(availableExerciseTypes.length);
      hasSetTotal.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableExerciseTypes.length]);

  useEffect(() => {
    hasSetTotal.current = false;
  }, [exercise?.id]);

  if (isLoading) {
    return (
      <div>
        <SpinnerLoader />
      </div>
    );
  }

  if (error) {
    return <div>Error loading exercises.</div>;
  }

  if (!isExerciseStarted || !exercise) {
    return null;
  }

  return (
    <DndProvider backend={isSmall ? TouchBackend : HTML5Backend}>
      <div>
        <RenderExerciseComponent
          {...{ isSmall, exerciseData, currentExerciseType }}
        />
      </div>
    </DndProvider>
  );
};

export default ExercisesContainer;
